import { Injectable, Pipe, PipeTransform } from '@angular/core';
import { MigrateDocument, NF_response } from '../../interface/NF-response';
import { NFSE_response, ResponseRPS } from '../../interface/NFSE-response';
import { Company } from '../../interface/company';
import { NFSe } from '../../interface/NFS-e';
import { Utilities } from '../../class/utilities';
import { NFCe } from '../../interface/NFC-e';
import { NFe } from '../../interface/NF-e';
import { INVOICE_STATE } from '../lists/invoice-state';
import { MigrateQueryDocument } from 'src/app/interface/fiscal/migrate-query-response';
import { MkgOS } from 'src/app/class/mkg-os';


/**
 * Adjust the date format of the XML date string to a valid format.
 *
 * Some invoices are created (by other systems) with a wrong date format
 * @Ex: ""2023-09-21T09:20:4000:00"
 */
function fixDate(xmlDate: string): string {
  return xmlDate.replace(/T(\d{2}):(\d{2}):(\d{2})\d{2}:\d{2}$/, 'T$1:$2:$3');
}

type AvailableProperties = "model" | "dhEmit" | "cnpj" | "serie" | "number" | "value" | "client" | "clientDocument" | "pdfStr" | "pdfLink" | "xmlLink" | "inOut" | "NFRef" | "additionalInfo" | "rejection" | "laborsValue" | "discountValue" | "freigthValue" | "partsValue" | "observation" | "isNFSE" | "eventPdfStr" | "eventPdfLink" | "xmlStr" | "numRps"

@Injectable({ providedIn: 'root'})
@Pipe({
  standalone: true,
  name: 'invoiceProperty'
})
export class InvoicePropertyPipe implements PipeTransform {
  /**
   *
   * @param invoice
   * @param property One of these
   *  * model
   *  * dhEmit
   *  * cnpj - The client/supplier cnpj of invoice
   *  * serie
   *  * number
   *  * value
   *  * client - require cnpj
   *  * clientDocument - require cnpj - available only for NFe/NFCe
   *  * pdfStr
   *  * pdfLink
   *  * xmlLink
   *  * inOut - require cnpj
   *  * NFRef - only for NFe/NFCe
   *  * additionalInfo - only for NFe/NFCe
   *  * rejection
   *  * laborsValue - only for NFSe
   *  * discountValue
   *  * freigthValue
   *  * partsValue
   *  * observation
   *  * isNFSE
   *  * eventPdfStr
   *  * eventPdfLink
   *  * xmlStr
   *  * numRps
   * @param company Required if you want the property "isOut" or "client"
   * @returns
   */
  transform(invoice: (NF_response | NFSE_response), property: AvailableProperties, company?: Company, osType?: MkgOS['type']): any {
    if (!invoice || !invoice.request || !invoice.request.Documento) {
      console.warn("Nota com erros de exibição", invoice)
      return undefined;
    }

    const isNFSE = (invoice && invoice.request && invoice.request.Documento && invoice.request.Documento.ModeloDocumento === 'NFSE');

    if (property === "isNFSE") {
      return isNFSE;
    }

    if (isNFSE) {
      let nfseGerencial = '';
      if (invoice.state == INVOICE_STATE.GERENCIAL) {
        nfseGerencial = 'OIS';
      }
      const nfse = invoice.request.Documento as NFSe;
      if (!nfse.RPS.length) {
        // avoid access an property of undefined value
        return undefined;
      }
      const [response1] = (invoice as NFSE_response).response && Array.isArray((invoice as NFSE_response).response.Documentos) ? (invoice as NFSE_response).response.Documentos : [];
      const [response2] = (invoice as NFSE_response).response2 && Array.isArray((invoice as NFSE_response).response2.Documentos) ? (invoice as NFSE_response).response2.Documentos : [];

      const cnpj = nfse.RPS[0].Prestador.CNPJ_prest || (nfse.RPS[0].Prestador as any).CNPJ;
      let isOut = true;
      if (company) {
        if (osType && osType?.nfseCnpj?.trim()) {
          isOut = Utilities.equalUnmasked(cnpj, osType.nfseCnpj);
        } else {
          isOut = Utilities.equalUnmasked(cnpj, company.cnpj);
        }
      }
      switch (property) {
        case 'model': return invoice.state == INVOICE_STATE.GERENCIAL ? nfseGerencial : nfse.ModeloDocumento;
        case 'dhEmit': return fixDate(nfse.RPS[0].dEmis);
        case 'cnpj': return cnpj;
        case 'serie': return nfse.RPS[0].RPSSerie;
        case 'number': return nfse.RPS[0].RPSNumero;
        case 'value': return Number(nfse.RPS[0].Servico.Valores.ValLiquido);
        case 'client':
          return isOut ? (nfse.RPS[0].Tomador ? nfse.RPS[0].Tomador.TomaRazaoSocial : '') : nfse.RPS[0].Prestador.xNome;
        case 'clientDocument':
          return isOut ? (nfse.RPS[0].Tomador ? (nfse.RPS[0].Tomador.TomaCNPJ || nfse.RPS[0].Tomador.TomaCPF) : '') : nfse.RPS[0].Prestador.CNPJ_prest;
        case 'pdfStr': return (response1 as ResponseRPS).DocPDFBase64 ?? (response1 as MigrateQueryDocument).DocPDF;
        case 'pdfLink':
          if (response1) {
            if (response1.DocImpPrefeitura) {
              return response1.DocImpPrefeitura;
            }
            if ((response1 as ResponseRPS).DocPDFDownload) {
              return (response1 as ResponseRPS).DocPDFDownload;
            }
            if ((response1 as MigrateQueryDocument).DocPDFLink) {
              return (response1 as MigrateQueryDocument).DocPDFLink;
            }
          }
          return '';
        case 'xmlStr': return ((response1 as ResponseRPS)?.DocXMLBase64 || (response1 as MigrateQueryDocument)?.DocXML) || "";
        case 'xmlLink':
          return (response1 as ResponseRPS)?.DocXMLDownload || (response1 as MigrateQueryDocument)?.DocXMLLink || "";
        case 'inOut': return isOut ? 'Saída' : 'Entrada';
        case 'rejection':
          if (response2?.Situacao) {
            return response2.Situacao.SitDescricao
          }
          if ((response1 as ResponseRPS)?.Situacao) {
            return (response1 as ResponseRPS).Situacao.SitDescricao;
          }
          return (response1 as MigrateQueryDocument)?.DocSitDescricao || invoice.response?.Descricao || ""
        case 'laborsValue':
          return Number(nfse.RPS[0].Servico.Valores.ValServicos) || 0;
        case 'discountValue':
          return (Number(nfse.RPS[0].Servico.Valores.ValDescCond) || 0) + (Number(nfse.RPS[0].Servico.Valores.ValDescIncond) || 0);
        case 'freigthValue':
        case 'partsValue':
          return 0;
        case 'numRps':
          return response1?.NFSe?.NFSeNumero ?? "";
        case 'observation': return nfse.RPS[0].NFSOutrasinformacoes;
        // case 'laborsValue':
        // return invoice.response.
        default: return invoice[property];
      }

    } else {
      if (!invoice || !invoice.request || !invoice.request.Documento) {
        console.warn("Nota com erros de exibição", invoice)
        return undefined
      }
      const nf = invoice.request.Documento as NFCe | NFe;
      const ICMStot = nf.total ? (nf.total.ICMStot || (nf as any).total.ICMSTot) : undefined;
      const [response1] = (invoice as NF_response).response && Array.isArray((invoice as NF_response).response.Documentos) ? (invoice as NF_response).response.Documentos : [];
      const [response2] = (invoice as NF_response).response2 && Array.isArray((invoice as NF_response).response2.Documentos) ? (invoice as NF_response).response2.Documentos : [];

      let docEmit = "";
      if (nf.emit) {
        docEmit = nf.emit.CNPJ_emit || (nf.emit as any).CNPJ || nf.emit.CPF_emit || (nf.emit as any).CPF || (nf.emit as any).idEstrangeiro;
      }
      let docDest = "";
      if (nf.dest) {
        docDest = nf.dest.CNPJ_dest || (nf.dest as any).CNPJ || (nf.dest as any).CPF || nf.dest.CPF_dest;
      }

      switch (property) {
        case 'model': {
          if (invoice.state == INVOICE_STATE.GERENCIAL && nf.ide.mod === '55') {
            return 'OIP'
          } else if (invoice.state == INVOICE_STATE.GERENCIAL && nf.ide.mod !== '55') {
            return 'OIP'
          } else if (invoice.state == INVOICE_STATE.GERENCIAL_DEVOLUTIONED && nf.ide.mod === '55') {
            return 'OIP'
          } else if (invoice.state == INVOICE_STATE.GERENCIAL_DEVOLUTIONED && nf.ide.mod !== '55') {
            return 'OIP'
          } else if (invoice.state == INVOICE_STATE.INPUT_WITHOUT_XML) {
            return 'OIM'
          }
          else {
            return nf.ModeloDocumento || (nf.ide.mod === '55' ? 'NFe' : 'NFCe');
          }
        }
        // case 'model': return nf.ModeloDocumento || (nf.ide.mod === '55' ? 'NFe' : 'NFCe');
        case 'dhEmit': return fixDate(nf.ide.dhEmi);
        case 'cnpj': return docEmit;
        case 'serie': return (nf.ide.serie ?? '').toString();
        case 'number': return (nf.ide.nNF ?? '').toString();
        case 'value': return ICMStot ? Number(ICMStot.vNF) : undefined;
        case 'client':
          if (Utilities.equalUnmasked(docEmit, company?.cnpj)) {
            // client is the dest
            if (nf.dest) {
              return nf.dest.xNome_dest || nf.dest['xNome'];
            }
            return 'Consumidor não identificado';
          }
          // client is the emit
          return nf.emit.xNome;
        case 'clientDocument':
          if (Utilities.equalUnmasked(docEmit, company?.cnpj)) {
            // client is the dest
            return docDest;
          }
          // client is the emit
          return docEmit
        case 'pdfStr':
          let respPdf = '';
          if (response1) {
            respPdf = (response1 as MigrateDocument).DocPDFBase64 || (response1 as MigrateQueryDocument).DocPDF;
          }
          return respPdf;
        case 'pdfLink':
          if (response1) {
            return (response1 as MigrateDocument).DocPDFDownload || (response1 as MigrateQueryDocument).DocPDFLink;
          }
          return '';
        case 'eventPdfStr':
          let resp2Pdf = '';
          if (response2) {
            resp2Pdf = response2.DocPDFBase64 || response2[0].DocPDF;
          }
          return resp2Pdf;
        case 'eventPdfLink':
          if (response2) {
            return response2.DocPDFDownload;
          }
          return '';
        case 'xmlStr':
          let respXml = '';
          if (response1) {
            respXml = (response1 as MigrateDocument).DocXMLBase64 || ((response1 as MigrateQueryDocument)).DocXML;
          }
          return respXml;
        case 'xmlLink':
          if (response1) {
            return (response1 as MigrateDocument).DocXMLDownload || (response1 as MigrateQueryDocument).DocXMLLink;
          }
          return '';
        case 'inOut': return (nf.ide.tpNf == 0 ? 'Entrada' : 'Saída');
        case 'NFRef':
          if (nf.ide && nf.ide.NFRef) {
            if (typeof nf.ide.NFRef === 'string') {
              return nf.ide.NFRef
            } else if (Array.isArray(nf.ide.NFRef)) {
              return (nf.ide.NFRef as any)[0].refNFe;
            } else {
              return (nf.ide.NFRef as any).refNFe;
            }
          }
          const referencedItem = (nf as NFe).det?.find(det => det.DFeReferenciado?.length)?.DFeReferenciado?.[0];
          if (referencedItem?.chaveAcesso) {
            return referencedItem.chaveAcesso;
          }
          if (nf.ide && (nf.ide as any).accessKey) {
            return nf.ide && (nf.ide as any).accessKey;
          }
          return '';
        case 'additionalInfo': return (nf as NFe).infAdic ? (nf as NFe).infAdic.infCpl : '';
        case 'freigthValue': return Number((ICMStot ? (ICMStot.vFrete_ttlnfe || ICMStot.vFrete) : '0')) || 0;
        case 'partsValue': return Number((ICMStot ? (ICMStot.vProd_ttlnfe || ICMStot.vProd) : '0')) || 0;
        case 'discountValue': return Number(ICMStot ? (ICMStot.vDesc_ttlnfe || ICMStot.vDesc) : '0') || 0;
        case 'laborsValue': return 0;
        case 'rejection':
          if ((invoice.response2?.Codigo === 241) && !!invoice.response2?.Descricao?.trim() && !response2) return invoice.response2?.Descricao;

          if (response2 && (response2 as MigrateDocument)?.Situacao) {
            return (response2 as MigrateDocument).Situacao?.SitDescricao;
          } else if (response1) {
            if ((response1 as MigrateDocument)?.Situacao) {
              return (response1 as MigrateDocument).Situacao?.SitDescricao;
            }
            return (response1 as MigrateQueryDocument)?.DocSitDescricao || '';
          }
          return invoice.response.Descricao || '';
        case 'observation': return (nf as NFe).infAdic ? (nf as NFe).infAdic.infCpl : ''
        default: return invoice[property];
      }
    }
  }
}

